import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const SERVICE_TYPES = [
  'Vor Ort Service',
  'Remote Service',
  'Telefon Service',
  'Fernwartung',
  'Office',
];

function calcDuration(start, end) {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return m + ' Min';
  if (m === 0) return h + ' Std';
  return h + ' Std ' + m + ' Min';
}

function isZeroDuration(duration) {
  if (!duration) return false;
  const nums = String(duration).match(/\d+/g);
  if (!nums) return false;
  return nums.every(function(n) { return Number(n) === 0; });
}

function today() {
  return new Date().toISOString().split('T')[0];
}

function formatDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function ServiceReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editReport, setEditReport] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadReports = useCallback(async () => {
    try {
      const res = await api.get('/service-reports');
      setReports(res.data);
    } catch (e) {
      setError('Berichte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  function openNew() {
    setEditReport(null);
    setForm({
      ticket_id: '',
      service_date: today(),
      service_type: 'Vor Ort Service',
      time_start: '',
      time_end: '',
      duration: '',
      work_description: '',
      salutation: '',
      contact_name: '',
      recipient_email: '',
      customer_name_preview: '',
    });
    setError('');
    setSuccess('');
    setShowForm(true);
  }

  function openEdit(r) {
    setEditReport(r);
    setForm({
      ticket_id: r.ticket_id,
      service_date: r.service_date ? r.service_date.split('T')[0] : today(),
      service_type: r.service_type,
      time_start: r.time_start || '',
      time_end: r.time_end || '',
      duration: r.duration || '',
      work_description: r.work_description,
      salutation: r.salutation || '',
      contact_name: r.contact_name || '',
      recipient_email: r.recipient_email || '',
      customer_name_preview: r.customer_name || '',
    });
    setError('');
    setSuccess('');
    setShowForm(true);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'time_start' || name === 'time_end') {
        updated.duration = calcDuration(
          name === 'time_start' ? value : prev.time_start,
          name === 'time_end' ? value : prev.time_end
        );
      }
      return updated;
    });
  }

  async function loadTicketDefaults(ticketId) {
    if (!ticketId) return;
    setError('');
    try {
      await api.get('/service-reports/by-ticket/' + ticketId).then(r => r.data);
      setError('Fuer dieses Ticket existiert bereits ein Servicebericht.');
    } catch (e) {
      try {
        const ticketRes = await api.get('/tickets/' + ticketId);
        const ticketData = ticketRes.data;
        setForm(prev => ({
          ...prev,
          customer_name_preview: ticketData.customer_name || '',
          recipient_email: ticketData.contact_email || ticketData.customer_email || prev.recipient_email,
          contact_name: ticketData.contact_name || prev.contact_name,
          salutation: ticketData.salutation_text || prev.salutation,
        }));
      } catch (_) {}
    }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      if (editReport) {
        await api.put('/service-reports/' + editReport.id, form).then(r => r.data);
      } else {
        await api.post('/service-reports', form).then(r => r.data);
      }
      setShowForm(false);
      setSuccess(editReport ? 'Bericht gespeichert.' : 'Bericht erstellt.');
      loadReports();
    } catch (e) {
      setError(e.message || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSend(id) {
    if (!window.confirm('Bericht jetzt per E-Mail versenden?')) return;
    setSending(id);
    setError('');
    try {
      await api.post('/service-reports/' + id + '/send').then(r => r.data);
      setSuccess('Bericht erfolgreich versendet.');
      loadReports();
    } catch (e) {
      setError(e.message || 'Fehler beim Versenden.');
    } finally {
      setSending(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Bericht wirklich loeschen?')) return;
    try {
      await api.delete('/service-reports/' + id).then(r => r.data);
      setSuccess('Bericht geloescht.');
      loadReports();
    } catch (e) {
      setError(e.message || 'Fehler beim Loeschen.');
    }
  }

  const apiBase = process.env.REACT_APP_API_URL || '';

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Serviceberichte</h1>
        <button
          onClick={openNew}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          + Neuer Bericht
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>
      )}

      {!showForm && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Lade Berichte...</div>
          ) : reports.length === 0 ? (
            <div className="p-8 text-center text-gray-400">Noch keine Serviceberichte vorhanden.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Ticket</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Kunde</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Datum</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Service-Typ</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map(r => (
                  <tr key={r.id} className={isZeroDuration(r.duration) ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50"}>
                    <td className="px-4 py-3 font-mono text-blue-600">{r.ticket_number}</td>
                    <td className="px-4 py-3 text-gray-800">{r.customer_name}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(r.service_date)}</td>
                    <td className="px-4 py-3 text-gray-600">{r.service_type}</td>
                    <td className="px-4 py-3">
                      {r.sent ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">
                          Gesendet {r.sent_at ? formatDate(r.sent_at) : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 font-medium">
                          Entwurf
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            const res = await api.get('/service-reports/' + r.id + '/preview');
                            const blob = new Blob([res.data], { type: 'text/html' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          }}
                          className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 text-gray-600"
                        >
                          Vorschau
                        </button>
                        <button
                          onClick={() => openEdit(r)}
                          className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 text-gray-600"
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleSend(r.id)}
                          disabled={sending === r.id}
                          className="text-xs px-2 py-1 rounded border border-blue-200 hover:bg-blue-50 text-blue-600"
                        >
                          {sending === r.id ? '...' : 'Senden'}
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="text-xs px-2 py-1 rounded border border-red-200 hover:bg-red-50 text-red-500"
                        >
                          Loeschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showForm && form && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-800">
              {editReport ? 'Bericht bearbeiten - ' + editReport.ticket_number : 'Neuer Servicebericht'}
            </h2>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">x</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!editReport && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket-ID *</label>
                <input
                  type="number"
                  name="ticket_id"
                  value={form.ticket_id}
                  onChange={handleChange}
                  onBlur={e => loadTicketDefaults(e.target.value)}
                  placeholder="z.B. 42"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {form.customer_name_preview && (
                  <p className="text-xs text-green-600 mt-1">Kunde: {form.customer_name_preview}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Datum *</label>
              <input
                type="date"
                name="service_date"
                value={form.service_date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service-Typ *</label>
              <select
                name="service_type"
                value={form.service_type}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {SERVICE_TYPES.map(stype => (
                  <option key={stype}>{stype}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Beginn</label>
              <input
                type="time"
                name="time_start"
                value={form.time_start}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ende</label>
              <input
                type="time"
                name="time_end"
                value={form.time_end}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gesamtzeit <span className="text-gray-400 font-normal">(automatisch berechnet, editierbar)</span>
              </label>
              <input
                type="text"
                name="duration"
                value={form.duration}
                onChange={handleChange}
                placeholder="z.B. 1 Std 30 Min"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {isZeroDuration(form.duration) && (
                <p className="text-red-600 text-sm font-bold mt-1">⚠ Kulanzleistung – keine Berechnung dieser Zeit</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Durchgefuehrte Arbeiten *</label>
              <textarea
                name="work_description"
                value={form.work_description}
                onChange={handleChange}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2 border-t border-gray-100 pt-4 mt-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">E-Mail-Daten</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anrede</label>
              <input
                type="text"
                name="salutation"
                value={form.salutation}
                onChange={handleChange}
                placeholder="Sehr geehrte Frau Mustermann,"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ansprechpartner</label>
              <input
                type="text"
                name="contact_name"
                value={form.contact_name}
                onChange={handleChange}
                placeholder="Max Mustermann"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Empfaenger E-Mail</label>
              <input
                type="email"
                name="recipient_email"
                value={form.recipient_email}
                onChange={handleChange}
                placeholder="kunde@beispiel.de"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-5 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm text-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
